import React from 'react';
import cn from 'classnames';
import { format, isSameDay } from 'date-fns';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import Label from '@dealroadshow/uikit/core/components/Badge/Label';
import IconFilePdf from '@dealroadshow/uikit/core/components/Icon/IconFilePdf';
import { OTHER_SECTOR } from '@/finsight/domain/Sector';
import { MONTH_DAY_YEAR_FORMAT, TIME_FORMAT_WITH_TIMEZONE } from '@/Framework/dateHelpers/dateFormats';
import utcToZonedDate from '@/Framework/dateHelpers/utcToZonedDate';

import styles from '@/finsight/ui/components/research/common/ResearchTable/researchTable.scss';
import linkStyles from '@dealroadshow/uikit/core/styles/links.scss';
import alignStyles from '@dealroadshow/uikit/core/styles/helpers/align.scss';

const getTableColumns = (onDocumentClick) => [
  {
    name: 'pseudoSectorCollection',
    title: 'Sector',
    className: styles.colSector,
    width: 50,
    cellCallback: (cellProps) => {
      const pseudoSector = cellProps.cellData[0];
      const sectorOTHR = cellProps.cellData.find(
        (pseudoSector) => pseudoSector.abbreviation === OTHER_SECTOR.abbreviation,
      );
      const sectorColor = sectorOTHR ? sectorOTHR.color : pseudoSector.color;
      const sectorAbbr = sectorOTHR ? sectorOTHR.abbreviation : pseudoSector.abbreviation;
      const tooltipContent = !pseudoSector.pseudoSubsectorCollection.length
        ? pseudoSector.name
        : `${ pseudoSector.name }: ${ pseudoSector.pseudoSubsectorCollection[0].name }`;

      return (
        <Tooltip content={ tooltipContent }>
          <Label
            className={ styles.sectorLabel }
            backgroundColor={ sectorColor }
          >
            { sectorAbbr }
          </Label>
        </Tooltip>
      );
    },
  },
  {
    name: 'title',
    title: 'Name',
    className: styles.colName,
    sortable: true,
    cellCallback: (cellProps) => {
      const documentId = cellProps.row.id;
      const subtitle = cellProps.row.subtitle ? `: ${ cellProps.row.subtitle }` : '';
      const cellString = `${ cellProps.cellData }${ subtitle }`;

      return (
        <span
          className={ linkStyles.link }
          data-test="researchTableTitleCell"
          onClick={ async () => {
            const { documentUrl } = await onDocumentClick(documentId);
            window.gtag('event', 'abs_research', {
              link: documentUrl,
              research_name: cellProps.cellData,
              sector: cellProps.row.pseudoSectorCollection[0].name,
              subsector: cellProps.row.pseudoSectorCollection?.[0].pseudoSubsectorCollection?.[0]?.name || '',
              publisher: cellProps.row.publisher.displayName,
              publish_date: cellProps.row.publishedAt.dateTime,
              type: cellProps.row.documentTypeCollection.map((type) => type.displayName).join(', '),
            });
          } }
        >
          { cellString }
        </span>
      );
    },
  },
  {
    name: 'documentTypeCollection',
    title: 'Type',
    className: cn(styles.colType, alignStyles.alignRight),
    cellCallback: (cellProps) => cellProps.cellData.map((type) => type.displayName).join(', '),
  },
  {
    name: 'publisher',
    title: 'Publisher',
    className: cn(styles.colPublisher, alignStyles.alignRight),
    width: 100,
    cellCallback: (cellProps) => {
      let publisherName = '';
      let tooltip = '';

      if (cellProps.cellData.displayName === 'DBRS') {
        publisherName = `${ cellProps.cellData.displayName }-MS`;
        tooltip = `${ cellProps.cellData.displayName } - Morningstar`;
      } else {
        return cellProps.cellData.displayName;
      }

      return (
        <Tooltip content={ tooltip }>
          { publisherName }
        </Tooltip>
      );
    },
  },
  {
    name: 'publishedAt',
    title: 'Date',
    sortable: true,
    className: cn(styles.colDate, alignStyles.alignRight),
    width: 120,
    cellCallback: ({ row, cellData }) => {
      const documentId = row.id;
      const { timezone, dateTime } = cellData;
      const time = utcToZonedDate(timezone.timeZone, new Date(dateTime));
      const isToday = isSameDay(time, utcToZonedDate(timezone.timeZone));

      const publishedAt = isToday
          ? `${ format(time, TIME_FORMAT_WITH_TIMEZONE) } ${ timezone.abbr }`
          : format(time, MONTH_DAY_YEAR_FORMAT);

      return (
        <>
          <div className={ styles.publishedAt }>
            { publishedAt }
          </div>
          <div className={ styles.publishedAtActions }>
            <span
              data-test="researchTableDateCell"
              onClick={ async () => {
              const { documentUrl } = await onDocumentClick(documentId);
              window.gtag('event', 'abs_research', {
                link: documentUrl,
                research_name: row.title,
                sector: row.pseudoSectorCollection[0].name,
                subsector: row.pseudoSectorCollection?.[0].pseudoSubsectorCollection?.[0]?.name || '',
                publisher: row.publisher.displayName,
                publish_date: row.publishedAt.dateTime,
                type: row.documentTypeCollection.map((type) => type.displayName).join(', '),
              });
            } }
            >
              <IconFilePdf
                className={ styles.downloadDocumentIcon }
              />
            </span>
          </div>
        </>
      );
    },
  },
];

export default getTableColumns;
